import {
  type InfiniteData,
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect } from 'react';
import type { PageResponse } from '@/shared/types/abstract-api-response';
import { showToast } from '@/shared/utils/showToast';
import { useAuthStore } from '@/store/authStore';
import type {
  CommunityEntry,
  CommunityFeed,
  CommunityPost,
  LikeState,
} from '../types/community.types';

/** One community board (Donasi, Media): its query key roots and API calls. */
export interface CommunityBoard<P extends CommunityPost, C extends CommunityEntry, N> {
  /** ['<feedsKey>', user_id, feed] */
  feedsKey: string;
  /** ['<commentsKey>', user_id, post_id] */
  commentsKey: string;
  fetchPosts: (feed: CommunityFeed, page: number, size: number) => Promise<PageResponse<P>>;
  createPost: (input: N) => Promise<P>;
  deletePost: (postId: string) => Promise<void>;
  setPostLike: (postId: string, liked: boolean) => Promise<LikeState>;
  fetchComments: (postId: string, page: number, size: number) => Promise<PageResponse<C>>;
  createComment: (postId: string, content: string) => Promise<C>;
  deleteComment: (postId: string, commentId: string) => Promise<void>;
  setCommentLike: (postId: string, commentId: string, liked: boolean) => Promise<LikeState>;
}

const PAGE_SIZE = 10;
// The API maximum; every page is loaded when a comment section opens
const COMMENT_PAGE_SIZE = 50;

type Pages<T> = InfiniteData<PageResponse<T>>;

// Per user, so one account's likes never show up for the next on the same browser
const useFeedsKey = ({ feedsKey }: { feedsKey: string }) => [
  feedsKey,
  useAuthStore((state) => state.user_id),
];
const useCommentsKey = ({ commentsKey }: { commentsKey: string }) => [
  commentsKey,
  useAuthStore((state) => state.user_id),
];

const nextPage = (last: PageResponse<unknown>) =>
  last.page < last.total_pages ? last.page + 1 : undefined;

/** Applies `change` to every cached row; a null result drops the row. */
const updateRows = <T>(
  queryClient: QueryClient,
  queryKey: unknown[],
  change: (row: T) => T | null
) => {
  queryClient.setQueriesData<Pages<T>>({ queryKey }, (data) =>
    data
      ? {
          ...data,
          pages: data.pages.map((page) => {
            const rows = page.rows.map(change).filter((row): row is T => row !== null);
            return { ...page, rows, total: page.total - (page.rows.length - rows.length) };
          }),
        }
      : data
  );
};

const withLike = <T extends CommunityEntry>(row: T, { liked, like_count }: LikeState): T => ({
  ...row,
  liked_by_me: liked,
  like_count,
});

const optimisticLike = <T extends CommunityEntry>(row: T, liked: boolean): T =>
  row.liked_by_me === liked
    ? row
    : withLike(row, { liked, like_count: Math.max(0, row.like_count + (liked ? 1 : -1)) });

/** Timeline or Postingan Saya pages. */
export const useCommunityPages = <P extends CommunityPost, C extends CommunityEntry, N>(
  board: CommunityBoard<P, C, N>,
  feed: CommunityFeed
) => {
  const isLogin = useAuthStore((state) => state.isLogin);
  return useInfiniteQuery({
    queryKey: [...useFeedsKey(board), feed],
    queryFn: ({ pageParam }) => board.fetchPosts(feed, pageParam, PAGE_SIZE),
    initialPageParam: 1,
    getNextPageParam: nextPage,
    enabled: isLogin,
  });
};

/** Every comment of a post, oldest first; nothing is requested until `enabled`. */
export const useCommunityComments = <P extends CommunityPost, C extends CommunityEntry, N>(
  board: CommunityBoard<P, C, N>,
  postId: string,
  enabled: boolean
) => {
  const isLogin = useAuthStore((state) => state.isLogin);
  const query = useInfiniteQuery({
    queryKey: [...useCommentsKey(board), postId],
    queryFn: ({ pageParam }) => board.fetchComments(postId, pageParam, COMMENT_PAGE_SIZE),
    initialPageParam: 1,
    getNextPageParam: nextPage,
    enabled: isLogin && enabled,
  });

  const { hasNextPage, isFetchingNextPage, isError, fetchNextPage } = query;
  useEffect(() => {
    if (enabled && hasNextPage && !isFetchingNextPage && !isError) fetchNextPage();
  }, [enabled, hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  return query;
};

export const useCreateCommunityPost = <P extends CommunityPost, C extends CommunityEntry, N>(
  board: CommunityBoard<P, C, N>
) => {
  const queryClient = useQueryClient();
  const queryKey = useFeedsKey(board);
  return useMutation({
    mutationFn: (input: N) => board.createPost(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: (error) =>
      showToast({ type: 'error', title: 'Gagal mengirim postingan', message: error.message }),
  });
};

export const useDeleteCommunityPost = <P extends CommunityPost, C extends CommunityEntry, N>(
  board: CommunityBoard<P, C, N>
) => {
  const queryClient = useQueryClient();
  const queryKey = useFeedsKey(board);
  return useMutation({
    mutationFn: (postId: string) => board.deletePost(postId),
    onSuccess: (_, postId) =>
      updateRows<P>(queryClient, queryKey, (post) => (post.id === postId ? null : post)),
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey });
      showToast({ type: 'error', title: 'Gagal menghapus postingan', message: error.message });
    },
  });
};

/** Shows the like at once; only the answer to the last tap on a post is kept. */
export const useLikeCommunityPost = <P extends CommunityPost, C extends CommunityEntry, N>(
  board: CommunityBoard<P, C, N>
) => {
  const queryClient = useQueryClient();
  const queryKey = useFeedsKey(board);
  const mutationKey = [board.feedsKey, 'like'];
  return useMutation({
    mutationKey,
    mutationFn: ({ postId, liked }: { postId: string; liked: boolean }) =>
      board.setPostLike(postId, liked),
    onMutate: async ({ postId, liked }) => {
      await queryClient.cancelQueries({ queryKey });
      updateRows<P>(queryClient, queryKey, (post) =>
        post.id === postId ? optimisticLike(post, liked) : post
      );
    },
    onSuccess: (state, { postId }) => {
      const inFlight = queryClient.isMutating({
        mutationKey,
        predicate: (mutation) =>
          (mutation.state.variables as { postId?: string } | undefined)?.postId === postId,
      });
      if (inFlight === 1) {
        updateRows<P>(queryClient, queryKey, (post) =>
          post.id === postId ? withLike(post, state) : post
        );
      }
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey });
      showToast({ type: 'error', title: 'Gagal menyimpan suka', message: error.message });
    },
  });
};

/** Adds the comment; the post's comment section and counts reload. */
export const useCreateCommunityComment = <P extends CommunityPost, C extends CommunityEntry, N>(
  board: CommunityBoard<P, C, N>
) => {
  const queryClient = useQueryClient();
  const feedsKey = useFeedsKey(board);
  const commentsKey = useCommentsKey(board);
  return useMutation({
    mutationFn: ({ postId, content }: { postId: string; content: string }) =>
      board.createComment(postId, content),
    onSuccess: (_, { postId }) => {
      queryClient.invalidateQueries({ queryKey: [...commentsKey, postId] });
      queryClient.invalidateQueries({ queryKey: feedsKey });
    },
    onError: (error) =>
      showToast({ type: 'error', title: 'Gagal mengirim balasan', message: error.message }),
  });
};

export const useDeleteCommunityComment = <P extends CommunityPost, C extends CommunityEntry, N>(
  board: CommunityBoard<P, C, N>
) => {
  const queryClient = useQueryClient();
  const feedsKey = useFeedsKey(board);
  const commentsKey = useCommentsKey(board);
  return useMutation({
    mutationFn: ({ postId, commentId }: { postId: string; commentId: string }) =>
      board.deleteComment(postId, commentId),
    onSuccess: (_, { postId, commentId }) => {
      updateRows<C>(queryClient, [...commentsKey, postId], (comment) =>
        comment.id === commentId ? null : comment
      );
      updateRows<P>(queryClient, feedsKey, (post) =>
        post.id === postId ? { ...post, comment_count: Math.max(0, post.comment_count - 1) } : post
      );
    },
    onError: (error, { postId }) => {
      queryClient.invalidateQueries({ queryKey: [...commentsKey, postId] });
      queryClient.invalidateQueries({ queryKey: feedsKey });
      showToast({ type: 'error', title: 'Gagal menghapus komentar', message: error.message });
    },
  });
};

/** Shows the like at once; only the answer to the last tap on a comment is kept. */
export const useLikeCommunityComment = <P extends CommunityPost, C extends CommunityEntry, N>(
  board: CommunityBoard<P, C, N>
) => {
  const queryClient = useQueryClient();
  const commentsKey = useCommentsKey(board);
  const mutationKey = [board.commentsKey, 'like'];
  return useMutation({
    mutationKey,
    mutationFn: ({
      postId,
      commentId,
      liked,
    }: {
      postId: string;
      commentId: string;
      liked: boolean;
    }) => board.setCommentLike(postId, commentId, liked),
    onMutate: async ({ postId, commentId, liked }) => {
      const queryKey = [...commentsKey, postId];
      await queryClient.cancelQueries({ queryKey });
      updateRows<C>(queryClient, queryKey, (comment) =>
        comment.id === commentId ? optimisticLike(comment, liked) : comment
      );
    },
    onSuccess: (state, { postId, commentId }) => {
      const inFlight = queryClient.isMutating({
        mutationKey,
        predicate: (mutation) =>
          (mutation.state.variables as { commentId?: string } | undefined)?.commentId === commentId,
      });
      if (inFlight === 1) {
        updateRows<C>(queryClient, [...commentsKey, postId], (comment) =>
          comment.id === commentId ? withLike(comment, state) : comment
        );
      }
    },
    onError: (error, { postId }) => {
      queryClient.invalidateQueries({ queryKey: [...commentsKey, postId] });
      showToast({ type: 'error', title: 'Gagal menyimpan suka', message: error.message });
    },
  });
};
